import type {
  DagLayoutBounds,
  DagCategoryDefinition,
  DagLayoutInput,
  DagLayoutLane,
  DagLayoutOptions,
  DagLayoutResult,
  DagLayoutTick,
  DagRecordType,
} from "./types";

const LANE_GAP = 180;
const TOP_PADDING = 140;
const SIDE_PADDING = 180;
const MAX_NAMED_LANES = 12;

function hash01(value: string, salt = 0): number {
  let hash = 2166136261 ^ salt;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 0xffffffff;
}

function friendly(value: string): string {
  return value.replace(/^#/, "").replace(/[_-]+/g, " ").trim().toLowerCase() || "records";
}

function laneCandidate(node: DagLayoutInput["nodes"][number]): { key: string; label: string; order: number; group: string } {
  const kind = friendly(node.kind);
  const typeOrder: Record<DagRecordType, number> = {
    human: 0,
    operation: 2,
    version: 3,
    artifact: 3,
    lifecycle: 4,
    decision: 4,
  };
  if (node.semanticRole === "actor") {
    return { key: "human:actors", label: "actors", order: 0, group: "human-provenance" };
  }
  if (node.semanticRole === "journal") {
    return { key: "human:journals", label: "journals", order: 0.5, group: "human-provenance" };
  }
  if (node.semanticRole === "decision") {
    return { key: "human:decisions", label: "decisions", order: 1, group: "human-provenance" };
  }
  if (node.semanticRole === "decision_evidence") {
    return { key: "human:evidence", label: "decision evidence", order: 1.25, group: "human-provenance" };
  }
  if (node.semanticRole === "decision_status") {
    return { key: "human:decision-status", label: "decision history", order: 1.5, group: "human-provenance" };
  }
  if (node.type === "human") {
    const label = friendly(node.channel ?? kind);
    return { key: `human:${label}`, label, order: typeOrder.human, group: "human" };
  }
  if (node.type === "operation") {
    return { key: "operation:runs", label: "operation runs", order: typeOrder.operation, group: "operation" };
  }
  if (node.type === "lifecycle" || node.type === "decision") {
    return { key: "event:lifecycle", label: "lifecycle", order: typeOrder[node.type], group: "event" };
  }
  const engineeringLane = (() => {
    if (/geometry/.test(kind)) return "geometry";
    if (/mesh/.test(kind)) return "meshes";
    if (/plot|chart/.test(kind)) return "plots";
    if (/image|render|screenshot/.test(kind)) return "images · renders";
    if (/force|coefficient|dataset|result|metric/.test(kind)) return "datasets · results";
    if (/config|condition|quantit|setting|sweep|requirement/.test(kind)) return "inputs · configuration";
    if (/report|review|presentation|recipe|ledger|snapshot|index/.test(kind)) return "records · reports";
    return "artifacts";
  })();
  return { key: `${node.type}:${engineeringLane}`, label: engineeringLane, order: typeOrder[node.type], group: "engineering" };
}

export function resolveDagCategories(nodes: DagLayoutInput["nodes"]): {
  categories: DagCategoryDefinition[];
  categoryKeyByNode: string[];
} {
  const candidates = nodes.map(laneCandidate);
  const counts = new Map<string, number>();
  for (const candidate of candidates) counts.set(candidate.key, (counts.get(candidate.key) ?? 0) + 1);

  const definitions = [...new Map(candidates.map((candidate) => [candidate.key, candidate])).values()]
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  const protectedDefinitions = definitions.filter((definition) => definition.group === "human-provenance");
  const kept = definitions.length <= MAX_NAMED_LANES
    ? definitions
    : [
        ...protectedDefinitions,
        ...definitions
          .filter((definition) => definition.group !== "human-provenance")
          .sort((a, b) => (counts.get(b.key) ?? 0) - (counts.get(a.key) ?? 0) || a.key.localeCompare(b.key))
          .slice(0, Math.max(0, MAX_NAMED_LANES - 1 - protectedDefinitions.length)),
      ];
  const keptKeys = new Set(kept.map((definition) => definition.key));
  const collapsed = definitions.length > kept.length;
  const ordered = definitions.filter((definition) => keptKeys.has(definition.key));
  if (collapsed) ordered.push({ key: "other:records", label: "other records", order: 5, group: "other" });
  ordered.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));

  return {
    categories: ordered.map(({ key, label }) => ({ key, label })),
    categoryKeyByNode: candidates.map((candidate) => keptKeys.has(candidate.key) ? candidate.key : "other:records"),
  };
}

function buildLanes(nodes: DagLayoutInput["nodes"], options: DagLayoutOptions): { lanes: DagLayoutLane[]; laneByNode: number[] } {
  const { categories, categoryKeyByNode } = resolveDagCategories(nodes);
  const hidden = new Set(options.hiddenCategoryKeys ?? []);
  const preferredOrder = new Map((options.categoryOrder ?? []).map((key, index) => [key, index]));
  const defaultOrder = new Map(categories.map((category, index) => [category.key, index]));
  const ordered = categories
    .filter((category) => !hidden.has(category.key))
    .sort((a, b) => {
      const aPreferred = preferredOrder.get(a.key);
      const bPreferred = preferredOrder.get(b.key);
      if (aPreferred != null || bPreferred != null) {
        if (aPreferred == null) return 1;
        if (bPreferred == null) return -1;
        return aPreferred - bPreferred;
      }
      return (defaultOrder.get(a.key) ?? 0) - (defaultOrder.get(b.key) ?? 0);
    });
  const laneIndex = new Map(ordered.map((definition, index) => [definition.key, index]));
  const lanes = ordered.map((definition, index) => ({ ...definition, y: TOP_PADDING + index * LANE_GAP }));
  const laneByNode = categoryKeyByNode.map((key) => laneIndex.get(key) ?? -1);
  return { lanes, laneByNode };
}

/** Deterministic Kahn layering. Cyclic residues are placed after their deepest predecessor. */
export function topologicalLayers(graph: DagLayoutInput): Int32Array {
  const nodeCount = graph.nodes.length;
  const indegree = new Int32Array(nodeCount);
  const outgoing = Array.from({ length: nodeCount }, () => [] as number[]);
  for (const edge of graph.edges) {
    if (edge.source < 0 || edge.target < 0 || edge.source >= nodeCount || edge.target >= nodeCount) continue;
    indegree[edge.target] = indegree[edge.target]! + 1;
    outgoing[edge.source]!.push(edge.target);
  }
  for (const targets of outgoing) targets.sort((a, b) => a - b);

  const layer = new Int32Array(nodeCount);
  const queue: number[] = [];
  for (let index = 0; index < nodeCount; index += 1) {
    if (indegree[index] === 0) queue.push(index);
  }
  const processed = new Uint8Array(nodeCount);
  for (let head = 0; head < queue.length; head += 1) {
    const source = queue[head]!;
    processed[source] = 1;
    for (const target of outgoing[source] ?? []) {
      layer[target] = Math.max(layer[target]!, layer[source]! + 1);
      indegree[target] = indegree[target]! - 1;
      if (indegree[target] === 0) queue.push(target);
    }
  }

  let maxLayer = 0;
  for (const value of layer) maxLayer = Math.max(maxLayer, value);
  for (let index = 0; index < nodeCount; index += 1) {
    if (!processed[index]) layer[index] = maxLayer + 1 + Math.floor(hash01(graph.nodes[index]!.id, 71) * 3);
  }
  return layer;
}

function timestampValue(value: string | null): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function layoutTicks(min: number, max: number, width: number): DagLayoutTick[] {
  if (max <= min) return [];
  const formatter = new Intl.DateTimeFormat("en", { month: "short", day: "2-digit" });
  return Array.from({ length: 6 }, (_, index) => {
    const ratio = index / 5;
    const timestamp = min + ratio * (max - min);
    return { x: SIDE_PADDING + ratio * width, label: formatter.format(new Date(timestamp)) };
  });
}

export function computeDagLayout(graph: DagLayoutInput, options: DagLayoutOptions = {}): DagLayoutResult {
  const nodeCount = graph.nodes.length;
  const { lanes, laneByNode } = buildLanes(graph.nodes, options);
  const layers = topologicalLayers(graph);
  let maxLayer = 0;
  for (const value of layers) maxLayer = Math.max(maxLayer, value);

  const timestamps = graph.nodes.map((node, index) => laneByNode[index] === -1 ? null : timestampValue(node.timestamp));
  const finiteTimes = timestamps.filter((value): value is number => value != null).sort((a, b) => a - b);
  const visibleNodeCount = laneByNode.reduce((count, lane) => count + Number(lane >= 0), 0);
  const temporal = finiteTimes.length >= 2 && finiteTimes.length >= Math.min(visibleNodeCount, Math.max(2, Math.ceil(visibleNodeCount * 0.15)));
  const minTime = finiteTimes[0] ?? 0;
  const maxTime = finiteTimes[finiteTimes.length - 1] ?? minTime;
  const temporalWidth = Math.max(2_400, Math.min(18_000, Math.sqrt(Math.max(1, nodeCount)) * 155));
  const layeredWidth = Math.max(2_200, (maxLayer + 1) * 240);
  const contentWidth = temporal ? temporalWidth : layeredWidth;
  const maxY = TOP_PADDING + Math.max(0, lanes.length - 1) * LANE_GAP;
  const bounds: DagLayoutBounds = {
    minX: 0,
    minY: 0,
    maxX: contentWidth + SIDE_PADDING * 2,
    maxY: Math.max(1_100, maxY + TOP_PADDING),
  };
  const positions = new Float32Array(nodeCount * 2);
  positions.fill(Number.NaN);

  for (let index = 0; index < nodeCount; index += 1) {
    if (laneByNode[index] === -1) continue;
    const node = graph.nodes[index]!;
    const time = timestamps[index];
    const layerRatio = maxLayer > 0 ? layers[index]! / maxLayer : 0.5;
    const timeRatio = temporal && time != null && maxTime > minTime ? (time - minTime) / (maxTime - minTime) : layerRatio;
    const xJitter = (hash01(node.id, 11) - 0.5) * Math.min(42, contentWidth / Math.max(20, nodeCount));
    const yJitter = (hash01(node.id, 29) - 0.5) * 96;
    positions[index * 2] = SIDE_PADDING + Math.max(0, Math.min(1, timeRatio)) * contentWidth + xJitter;
    positions[index * 2 + 1] = lanes[laneByNode[index]!]!.y + yJitter;
  }

  return {
    positions,
    bounds,
    lanes,
    ticks: temporal ? layoutTicks(minTime, maxTime, contentWidth) : [],
    temporal,
  };
}
